import React, { useState } from 'react';
import {
  ArrowUpRight,
  Globe2,
  ImageIcon,
  LockKeyhole,
  PencilLine
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';

export const ProjectCard = ({ project, onEdit }) => {
  const { t, formatDate } = useI18n();
  const navigate = useNavigate();
  const [coverFailed, setCoverFailed] = useState(false);
  const isPublic = project.visibility === 'public';
  const openWorkspace = () => navigate(`/workspace/${project.id}`);
  const stopPropagation = (event) => event.stopPropagation();
  const description = project.description?.trim() || t('newProjectHint');
  const showCover = Boolean(project.coverImageUrl) && !coverFailed;

  return (
    <article
      className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white transition hover:border-neutral-300 hover:bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700 dark:hover:bg-neutral-900/50"
      onClick={openWorkspace}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openWorkspace();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-neutral-100 dark:bg-neutral-900">
        {showCover ? (
          <img
            alt=""
            src={project.coverImageUrl}
            loading="lazy"
            onError={() => setCoverFailed(true)}
            className="h-full w-full object-cover object-top transition duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-neutral-300 dark:text-neutral-700">
            <ImageIcon size={28} />
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-1 text-sm font-semibold tracking-tight text-neutral-950 dark:text-white">
              {project.name}
            </h3>
            <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-neutral-400">
              {isPublic ? <Globe2 size={11} /> : <LockKeyhole size={11} />}
              {isPublic ? t('public') : t('private')}
            </span>
          </div>
          <ArrowUpRight
            size={15}
            className="mt-0.5 shrink-0 text-neutral-300 opacity-0 transition group-hover:opacity-100 dark:text-neutral-600"
          />
        </div>

        <p className="mt-3 line-clamp-2 flex-1 text-xs leading-5 text-neutral-500">{description}</p>

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-neutral-100 pt-3 dark:border-neutral-900">
          <p className="text-[11px] text-neutral-400">{formatDate(project.updatedAt, { dateStyle: 'medium' })}</p>
          <button
            type="button"
            onClick={(event) => {
              stopPropagation(event);
              onEdit?.(project);
            }}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
            aria-label={t('projectSettings')}
          >
            <PencilLine size={14} />
          </button>
        </div>
      </div>
    </article>
  );
};
